import { Proto } from "../../../pojo/Proto";
import { Task } from "../../../pojo/Task";
import { AnswerType } from "../../data-ingestion/data.ingestion.service";
import type { FieldType } from "../../data-ingestion/data.ingestion.const";

export namespace Workflow {
    export interface WorkflowProcess {
        workflowProcessId?: number;
        workflowProcessName: string;
        description: string;
        enabled: boolean;
        createDate?: Proto.ISODateString;
        lastModDate?: Proto.ISODateString;
        lastModUser?: string;
        stages?: WorkflowStage[];
        stageTransitions?: WorkflowStageTransition[];
    }

    export type ProcessInstance = WorkflowProcess & { workflowProcessInstanceId: number };

    export interface WorkflowStage {
        workflowStageId?: number;
        workflowProcessId?: number;
        stageName: string;
        isTerminal?: boolean;
        isStartStage?: boolean; // True for the start stage (only one per process)
        isAccumulator?: boolean; // True for accumulator stages (waits for spawned child workflows)
        isApAccumulator?: boolean; // True for AP accumulator stages (waits for 25Live assignment policy)
        hasObjectApprovals?: boolean;
        description: string;
        defaultEnabled?: boolean;
        sortOrder?: number;
        createDate?: Proto.ISODateString;
        lastModDate?: Proto.ISODateString;
        lastModUser?: string;
        // Frontend-only fields
        id?: number; // Frontend ID for UI (temp negative IDs)
        states?: WorkflowState[]; // States belonging to this stage
        actions?: WorkflowAction[]; // Actions for terminal stages
    }

    export interface WorkflowState {
        workflowStateId?: number;
        workflowProcessId?: number;
        workflowStageId?: number; // Reference to parent stage
        stateName: string;
        description: string;
        defaultEnabled?: boolean;
        approvalGroupId?: number;
        hasAiApprover?: boolean;
        aiCanApprove?: boolean;
        aiCanDeny?: boolean;
        aiCanRequestChanges?: boolean;
        createDate?: Proto.ISODateString;
        lastModDate?: Proto.ISODateString;
        lastModUser?: string;
        // Frontend-only fields
        id?: number; // Frontend ID for UI (temp negative IDs)
        contacts?: WorkflowStateContact[]; // Contacts array (same format as backend)
        orgRoles?: WorkflowStateOrgRole[]; // Organization roles array (same format as backend)
    }

    export interface WorkflowStateOrgRole {
        workflowStateId?: number;
        contRoleId: number;
        contRole?: {
            roleName: string;
        };
    }

    export interface WorkflowStateContact {
        workflowStateId?: number;
        contId: number;
        contactName?: string; // Frontend-only contact name
    }

    export interface WorkflowStageTransition {
        workflowStageTransitionId?: number;
        workflowProcessId?: number;
        fromStageId?: number;
        toStageId?: number;
        fromStageName: string; // Used for frontend stage mapping
        toStageName: string; // Used for frontend stage mapping
        transitionLabel: string;
        transitionType: "APPROVE" | "DENY" | "REQUEST_CHANGES" | "CANCEL";
        description: string;
        createDate?: Proto.ISODateString;
        lastModDate?: Proto.ISODateString;
        lastModUser?: string;
        // Frontend-only fields
        id?: number; // Frontend ID for UI (temp negative IDs)
    }

    export interface WorkflowAction {
        workflowActionId?: number;
        workflowProcessId?: number;
        workflowStageId?: number; // Now associated with stages, not states
        actionItemTypeId?: number; // Required for field context
        actionItemId?: number; // Required for field context
        actionType: "HTTP_REQUEST" | "SYSTEM_ACTION";
        actionCode?:
            | "SEND_EMAIL"
            | "CREATE_EVENT"
            | "EDIT_EVENT"
            | "SPAWN_WORKFLOW"
            | "REGISTER"
            | "SET_ORG_STATUS"
            | "SET_CONTACT_ROLE"
            | "ADD_CONTACT_ROLES"
            | "CREATE_ORG"
            | "EDIT_ORG"
            | "ADD_CATEGORIES"
            | "ADD_CUSTOM_ATTRIBUTES"
            | "ADD_CANDIDATE"
            | "ADD_ORG_CATEGORIES"
            | "ADD_ORG_CUSTOM_ATTRIBUTES"
            | "ASSIGN_LIVE_25_DRAFT_EVENT"
            | "SET_SECURITY_GROUP"; // For system actions - matches ActionCode enum
        isSystemAction?: boolean; // True for system-defined actions
        sortOrder?: number;
        httpUrl?: string;
        httpMethod?: string;
        httpHeaders?: string;
        jsonPayloadTemplate?: string;
        description: string;
        defaultEnabled?: boolean; // Default enabled status for workflow action
        createDate?: Proto.ISODateString;
        lastModDate?: Proto.ISODateString;
        lastModUser?: string;
        ruleKey?: number; // Frontend-only client key so rules can be stored before the action is saved
        // Frontend-only fields
        id?: number; // Frontend ID for UI (temp negative IDs)
        inputs?: WorkflowActionInput[]; // System action inputs
        // Spawn workflow configuration
        spawnWorkflowProcessId?: number; // Which workflow to spawn (for SPAWN_WORKFLOW actions)
        spawnActionHandling?:
            | "KEEP_ALL"
            | "SKIP_ALL"
            | "SKIP_TERMINAL"
            | "MOVE_TERMINAL_TO_PARENT"
            | "MOVE_ALL_TO_PARENT"; // How to handle spawned workflow actions
        spawnReuseSameItem?: boolean; // If true, compose stages into current process instead of spawning new workflow
        spawnChildParentItemId?: number; // Parent item for child workflow (akin to WorkflowItem.getParentItemId). Can be either an ingestion point id or a budget plan id
        spawnChildParentItemTypeId?: number; // Parent item type for child workflow (akin to WorkflowItem.getParentItemTypeId). Type of the parent item (e.g., ingestion point type or budget plan type)
        // Spawn iterator configuration -- when set, iterates over an array field and spawns per-entry
        spawnIteratorFieldDefinitionId?: number; // fieldDefinitionId of the JSON array field to iterate over
        spawnDispatchFieldType?: string; // Which FieldType to key on for dispatch (e.g., "ROLE")
        dispatchMappings?: WorkflowActionDispatch[]; // Dispatch mapping rows: value -> target form
    }

    export interface WorkflowActionDispatch {
        workflowActionId?: number;
        dispatchValue: string; // The matched value (e.g., "5" for roleId 5), or "__fallback__" for catch-all
        targetIngestionPointId?: number; // The form (ingestion point) to spawn to
    }

    export interface WorkflowActionInput {
        workflowActionInputId?: number;
        workflowActionId?: number;
        inputCode: string; // e.g., 'subject', 'body', 'to', 'from'
        inputValue: string; // The actual input value
        createDate?: Proto.ISODateString;
        lastModDate?: Proto.ISODateString;
        lastModUser?: string;
        // Frontend-only fields
        id?: number; // Frontend ID for UI (temp negative IDs)
    }

    export interface WorkflowActionHistory {
        workflowActionHistoryId?: number;
        instance?: string;
        workflowProcessInstanceId?: number;
        originalWorkflowStageId?: number;
        originalWorkflowActionId?: number;
        workflowItemTypeId?: number;
        workflowItemId?: number;
        workflowItemName?: string;
        actionType?: string;
        actionCode?: string;
        status?: "RUNNING" | "COMPLETED" | "ERROR";
        errorMessage?: string;
        errorStackTrace?: string;
        httpResponseCode?: number;
        httpResponseBody?: string;
        executionDate?: Proto.ISODateString;
        createDate?: Proto.ISODateString;
        lastModDate?: Proto.ISODateString;
        lastModUser?: string;
    }

    export type WorkflowStatus = "PENDING" | "IN_PROGRESS" | "COMPLETED" | "DENIED" | "REQUESTED_CHANGES" | "CANCELED";

    /** The current user's own todo state on an assigned item. DISMISSED = own dismiss; CANCELLED = system cancel. */
    export type MyTodoState = "IN_PROGRESS" | "APPROVED" | "DENIED" | "REQUESTED_CHANGES" | "DISMISSED" | "CANCELLED";

    export const WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE = 10_001;
    export const BUDGET_PLAN_OBJECT_TYPE = 12_001;
    export const WORKFLOW_PROCESS_TYPE_ID = 9001;

    export interface WorkflowSubmissionEntry {
        itemTypeId?: string;
        itemId?: string;
        itemName?: string;
        itemValue?: string;
        itemDataType?: string;
        fieldType?: string;
        extraData?: { [key: string]: string };
    }

    export interface WorkflowR25User {
        username?: string;
        contId?: number;
        contact?: {
            contId?: number;
            firstName?: string;
            familyName?: string;
            email?: string;
        };
    }

    export interface WorkflowItem {
        parentItemTypeId?: number;
        parentItemId?: number;
        itemTypeId?: number;
        itemId?: number;
        submissionIdUuid?: string;
        name?: string;
        workflowSubmissionList?: WorkflowSubmissionEntry[];
        workflowProcessId?: number;
        workflowProcessInstanceId?: number;
        parentProcessInstanceId?: number;
        currentWorkflowStageId?: number;
        currentWorkflowStage?: WorkflowStage; // only stageName, isAccumulator, and isApAccumulator are populated
        contId?: number;
        todoId?: number;
        todoName?: string;
        myTodoState?: MyTodoState;
        stateClosed?: boolean; // another assignee already approved, denied, or requested changes on this todo's state
        stateClosedAction?: MyTodoState; // the decision that closed the state
        stateClosedByName?: string; // who closed the state; absent when that contact isn't visible to the user
        organizationId?: number; // populated by assigned.json
        organizationName?: string; // populated by assigned.json; absent when the organization isn't visible to the user
        workflowStatus?: WorkflowStatus;
        lastModUser?: string;
        lastModDate?: Proto.ISODateString;
        createDate?: Proto.ISODateString;
        submissionDate?: Proto.ISODateString;
        submitted?: boolean;

        // Linked forms - populated on demand when viewing a form
        childrenItems?: WorkflowItem[]; // Forms spawned from this form
        parent?: WorkflowItem; // Parent form (null if root)

        contactName?: string;
        contactEmail?: string;
        workflowProcessName?: string;
        approverNames?: string[];

        // frontend-only fields
        itemTypeName?: string;
        submitterName?: string;
    }

    export type SystemAction = {
        actionCode: string;
        displayName: string;
        description: string;
        inputs?: SystemActionInput[];
    };

    export type SystemActionInput = {
        inputCode: string;
        label: string;
        type: "text" | "textarea" | "checkbox" | "richtext";
        required: boolean;
        help?: string;
        acceptedAnswerTypes?: AnswerType[];
        acceptedFieldTypes?: FieldType[];
    };

    type ProcessInstanceApproval = {
        contName: string;
        contId: number;
        stateName: string;
        approvalStatus: Task.State;
    };

    export type ProcessInstanceStatus = {
        currentStageId: number;
        currentStageName: string;
        stages: (WorkflowStage & { originalWorkflowStageId: number; approvals: ProcessInstanceApproval[] })[];
    };

    export type DiagramAiOutcome = "WAITING" | "APPROVED" | "DENIED" | "CHANGES_REQUESTED" | "DEFERRED";

    export interface DiagramPerson {
        contId: number;
        /** Absent when the viewer may not see this contact. */
        name?: string;
        approvalStatus?: Task.State;
        restricted: boolean;
        chattable: boolean;
        unavailableReason?: string;
        /** Present only when chattable. */
        chatUser?: DiagramChatUser;
    }

    export interface DiagramChatUser {
        username: string;
        firstName?: string;
        lastName?: string;
        email?: string;
    }

    export interface DiagramResolvedInput {
        inputCode: string;
        value?: string;
        fieldType?: FieldType;
    }

    export interface DiagramActionDetail {
        originalWorkflowActionId: number;
        actionType: WorkflowAction["actionType"];
        actionCode?: WorkflowAction["actionCode"];
        inputs: DiagramResolvedInput[];
        httpRequest?: {
            method?: string;
            /** Full URL for Student Engagement admins; host only otherwise. */
            url?: string;
            /** Student Engagement admins only. */
            payload?: string;
        };
        execution?: {
            status?: WorkflowActionHistory["status"];
            executionDate?: Proto.ISODateString;
            errorMessage?: string;
            httpResponseCode?: number;
            /** Full-FLS viewers only. */
            httpResponseBody?: string;
        };
    }

    export interface DiagramSpawnedForm {
        itemTypeId: number;
        itemId: number;
        formId?: number;
        name?: string;
        submitted: boolean;
        assignee?: DiagramPerson;
        currentStageName?: string;
        approvers: DiagramPerson[];
    }

    export interface DiagramStageDetail {
        originalWorkflowStageId: number;
        reviewers: DiagramPerson[];
        aiOutcomes: { originalWorkflowStateId: number; stateName?: string; outcome: DiagramAiOutcome }[];
        usedActionIds: number[];
        actions: DiagramActionDetail[];
        spawnedForms: DiagramSpawnedForm[];
    }

    /** Response from GET /workflow/process-instance/{id}/diagram.json */
    export type ProcessInstanceDiagram = ProcessInstanceStatus & {
        workflowProcessInstanceId: number;
        workflowProcessName?: string;
        workflowStatus?: WorkflowStatus;
        submitter?: DiagramPerson;
        stageDetails: DiagramStageDetail[];
    };

    /** Response from GET /workflow/assigned-count.json */
    export interface AssignedWorkflowCountResponse {
        count: number;
    }

    /**
     * `scope` for GET /workflow/assigned.json.
     * - `active` (server default): only todos still awaiting the current user's action.
     * - `all`: every item ever assigned to the current user, including acted-on and closed ones.
     */
    export type AssignedWorkflowScope = "active" | "all";

    export interface AssignedWorkflowFacetOption {
        value: string;
        label: string;
    }

    /** Each facet is computed with every active filter except its own. */
    export interface AssignedWorkflowFacets {
        organizations: AssignedWorkflowFacetOption[];
        forms: AssignedWorkflowFacetOption[];
        submitters: AssignedWorkflowFacetOption[];
        statusCounts: Partial<Record<WorkflowStatus, number>>;
        actionableCount: number;
    }

    /** Response from GET /workflow/assigned.json when `page` is sent */
    export interface AssignedWorkflowPage {
        items: WorkflowItem[];
        totalCount: number;
        facets: AssignedWorkflowFacets;
    }

    export interface AssignedWorkflowFilters {
        keyword?: string;
        status?: WorkflowStatus;
        organizationId?: string;
        formName?: string;
        submitterContId?: string;
        submittedFrom?: Date;
        submittedTo?: Date;
        actionableOnly: boolean;
        sortAscending: boolean;
    }

    /**
     * JSON for optional system action input {@code actionConfig}; shared across actions.
     * Each action reads only the fields it uses.
     */
    export interface WorkflowActionConfig {
        skipAddingContactOrgRoles?: boolean;
    }
}
